// src/pages/loadletter/TemplateSettings.tsx

import React, { useState, useEffect } from 'react';
import { Save, RefreshCw, AlertTriangle, FileText, Settings as SettingsIcon } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  getDefaultTemplate,
  updateLoadLetterTemplate,
  getLoadLetterSettings,
} from '../../services/loadLetterService';
import { LoadLetterTemplate, LoadLetterSettings } from '../../types/loadLetter';
import Spinner from '../../components/ui/Spinner';
import RichTextEditor from '../../components/loadletter/RichTextEditor';
import { MEETING_SCHEDULE_FIELD, SIGNATURE_FIELD } from '../../components/loadletter/templateFields';

const TemplateSettings: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();

  const [template, setTemplate] = useState<LoadLetterTemplate | null>(null);
  const [settings, setSettings] = useState<LoadLetterSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  // Form state
  const [letterOpeningHtml, setLetterOpeningHtml] = useState<string>('');
  const [letterBodyHtml, setLetterBodyHtml] = useState<string>('');
  const [meetingScheduleHtml, setMeetingScheduleHtml] = useState<string>('');
  const [letterClosingHtml, setLetterClosingHtml] = useState<string>('');
  const [signatureHtml, setSignatureHtml] = useState<string>('');
  const [notesSectionHtml, setNotesSectionHtml] = useState<string>('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templateData, settingsData] = await Promise.all([
        getDefaultTemplate(),
        getLoadLetterSettings(),
      ]);

      if (templateData) {
        setTemplate(templateData);
        setLetterOpeningHtml(templateData.letterOpeningHtml || '');
        setLetterBodyHtml(templateData.letterBodyHtml || '');
        setMeetingScheduleHtml(templateData.meetingScheduleHtml || '');
        setLetterClosingHtml(templateData.letterClosingHtml || '');
        setSignatureHtml(templateData.signatureHtml || '');
        setNotesSectionHtml(templateData.notesSectionHtml || '');
      }

      setSettings(settingsData);
    } catch (error) {
      console.error('Error fetching template:', error);
      addToast('Failed to load template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!template || !user) {
      addToast('Template or user not loaded', 'error');
      return;
    }

    setSaving(true);
    try {
      const updatedTemplate: Partial<LoadLetterTemplate> = {
        letterOpeningHtml,
        letterBodyHtml,
        meetingScheduleHtml,
        letterClosingHtml,
        signatureHtml,
        notesSectionHtml,
        lastModifiedBy: user.uid || user.id,
      };

      await updateLoadLetterTemplate(template.id, updatedTemplate);
      addToast('Template updated successfully', 'success');
      await fetchData(); // Refresh
    } catch (error) {
      console.error('Error saving template:', error);
      addToast('Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!template) return;
    setLetterOpeningHtml(template.letterOpeningHtml || '');
    setLetterBodyHtml(template.letterBodyHtml || '');
    setMeetingScheduleHtml(template.meetingScheduleHtml || '');
    setLetterClosingHtml(template.letterClosingHtml || '');
    setSignatureHtml(template.signatureHtml || '');
    setNotesSectionHtml(template.notesSectionHtml || '');
    addToast('Form reset to saved values', 'info');
  };

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Template Settings"
          description="Edit load letter template content"
        />
        <Card>
          <div className="flex justify-center py-12">
            <Spinner size="lg" label="Loading template…" />
          </div>
        </Card>
      </div>
    );
  }

  if (!template) {
    return (
      <div>
        <PageHeader
          title="Template Settings"
          description="Edit load letter template content"
        />
        <Card>
          <div className="text-center py-12">
            <AlertTriangle className="h-12 w-12 text-warn-fg mx-auto mb-4" />
            <p className="text-ink-700 font-medium">No template found</p>
            <p className="text-sm text-ink-500 mt-2">
              Please ensure the default template has been created in the database
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Template Settings"
        description="Edit the content of load letters that get sent to staff"
        actions={
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              leftIcon={<RefreshCw className="h-4 w-4" />}
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button
              variant="primary"
              leftIcon={<Save className="h-4 w-4" />}
              onClick={handleSave}
              loading={saving}
            >
              Save Changes
            </Button>
          </div>
        }
      />

      {/* Info Card */}
      <Card className="mb-6">
        <div className="flex items-start space-x-3">
          <FileText className="h-5 w-5 text-navy-600 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-ink-900 mb-1">
              Template: {template.name}
            </h3>
            <p className="text-sm text-ink-500">
              {template.description}
            </p>
            <p className="text-xs text-ink-500 mt-2">
              Academic Year: {settings?.currentAcademicYear || 'N/A'} •
              Principal: {settings?.principalName || 'N/A'}
            </p>
          </div>
        </div>
      </Card>

      {/* Template Variables Reference */}
      <Card className="mb-6">
        <div className="flex items-start space-x-3">
          <SettingsIcon className="h-5 w-5 text-ink-500 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-ink-900 mb-2">
              Personalised fields
            </h3>
            <p className="text-sm text-ink-500">
              Edit each section below like a Word document. To add details that change for each staff member,
              such as their name or FTE, click <strong>+ Insert field</strong>. Fields appear as{' '}
              <span className="template-field-chip">blue tags</span> and are filled in when letters are generated.
              The class tables and allowance list are added to every letter automatically.
            </p>
          </div>
        </div>
      </Card>

      {/* Letter Opening Section */}
      <Card className="mb-6">
        <h3 className="text-card-title text-ink-900 mb-4">
          Letter Opening
        </h3>
        <p className="text-sm text-ink-500 mb-4">
          The opening paragraph of the letter (e.g., "Dear {'{{staffName}}'}, ...")
        </p>
        <RichTextEditor ariaLabel="Letter opening" value={letterOpeningHtml} onChange={setLetterOpeningHtml} />
      </Card>

      {/* Letter Body Section */}
      <Card className="mb-6">
        <h3 className="text-card-title text-ink-900 mb-4">
          Letter Body
        </h3>
        <p className="text-sm text-ink-500 mb-4">
          Main content of the principal's letter with bullet points and instructions
        </p>
        <RichTextEditor
          ariaLabel="Letter body"
          value={letterBodyHtml}
          onChange={setLetterBodyHtml}
          extraFields={[MEETING_SCHEDULE_FIELD]}
          minHeight={240}
        />
      </Card>

      {/* Meeting Schedule Section */}
      <Card className="mb-6">
        <h3 className="text-card-title text-ink-900 mb-4">
          Meeting Schedule
        </h3>
        <p className="text-sm text-ink-500 mb-4">
          The highlighted meeting schedule. It appears in the letter wherever the{' '}
          <span className="template-field-chip">Meeting schedule section</span> field is placed in the Letter Body.
        </p>
        <RichTextEditor ariaLabel="Meeting schedule" value={meetingScheduleHtml} onChange={setMeetingScheduleHtml} minHeight={160} />
      </Card>

      {/* Letter Closing Section */}
      <Card className="mb-6">
        <h3 className="text-card-title text-ink-900 mb-4">
          Letter Closing
        </h3>
        <p className="text-sm text-ink-500 mb-4">
          Closing remarks and thanks
        </p>
        <RichTextEditor ariaLabel="Letter closing" value={letterClosingHtml} onChange={setLetterClosingHtml} />
      </Card>

      {/* Signature Section */}
      <Card className="mb-6">
        <h3 className="text-card-title text-ink-900 mb-4">
          Signature Block
        </h3>
        <p className="text-sm text-ink-500 mb-4">
          Principal's signature and title
        </p>
        <RichTextEditor
          ariaLabel="Signature block"
          value={signatureHtml}
          onChange={setSignatureHtml}
          extraFields={[SIGNATURE_FIELD]}
        />
      </Card>

      {/* Notes Section */}
      <Card className="mb-6">
        <h3 className="text-card-title text-ink-900 mb-4">
          Additional Notes (Optional)
        </h3>
        <p className="text-sm text-ink-500 mb-4">
          Any additional notes or instructions section
        </p>
        <RichTextEditor ariaLabel="Additional notes" value={notesSectionHtml} onChange={setNotesSectionHtml} />
      </Card>

      {/* Warning about regeneration */}
      <Card>
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-warn-fg mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-ink-900 mb-1">
              Important Note
            </h3>
            <p className="text-sm text-ink-500">
              Changes to the template will only apply to newly generated letters.
              Existing letters that have already been generated will not be updated automatically.
              To apply these changes to existing letters, you will need to regenerate them.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TemplateSettings;
